/** Withdrawal queue (approve / reject / mark failed) and deposit list. Also used on a user's page. */
import type { AdminDeposit, AdminWithdrawal } from "@custos/api-client";
import {
  Address,
  Badge,
  Button,
  Card,
  EmptyState,
  ExternalLink,
  Field,
  Input,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  formatAmount,
  formatDateTime,
  type Tone,
} from "@custos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { usd } from "../lib/format";
import { useCan } from "../lib/permissions";
import { useDeposits, useWithdrawals } from "../lib/queries";

const W_TONE: Record<AdminWithdrawal["status"], Tone> = {
  REQUESTED: "warning",
  PENDING_APPROVAL: "warning",
  QUEUED: "primary",
  SIGNED: "primary",
  BROADCAST: "primary",
  CONFIRMED: "success",
  FAILED: "danger",
  REJECTED: "danger",
  CANCELLED: "neutral",
};

const D_TONE: Record<AdminDeposit["status"], Tone> = {
  DETECTED: "primary",
  CONFIRMING: "primary",
  CREDITED: "success",
  ORPHANED: "danger",
  DUST: "warning",
};

const label = (s: string) => s.replaceAll("_", " ").toLowerCase();

export function WithdrawalsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "OPEN";
  return (
    <>
      <PageHeader title="Withdrawals" subtitle="Approve or reject withdrawals that need a human check." />
      <Segmented
        label="Filter"
        value={status}
        onChange={(v) => setParams(v === "OPEN" ? {} : { status: v }, { replace: true })}
        className="mb-4"
        options={[
          { value: "OPEN", label: "Open" },
          { value: "PENDING_APPROVAL", label: "Needs approval" },
          { value: "FAILED", label: "Failed" },
          { value: "ALL", label: "All" },
        ]}
      />
      <WithdrawalList status={status} />
    </>
  );
}

type Action = { kind: "approve" | "reject" | "fail"; w: AdminWithdrawal } | null;

export function WithdrawalList({ status, userId }: { status: string; userId?: string }) {
  const can = useCan();
  const canReview = can("withdrawals.review");
  const canMarkFailed = can("withdrawals.mark_failed");
  const list = useWithdrawals(status, userId);
  const queryClient = useQueryClient();
  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["withdrawals"] });
    void queryClient.invalidateQueries({ queryKey: ["overview"] });
  };
  const open = (kind: NonNullable<Action>["kind"], w: AdminWithdrawal) => {
    setReason("");
    setTyped("");
    setAction({ kind, w });
  };

  if (list.isPending) return <Skeleton className="h-40 rounded-card" />;
  if (list.error) return <Notice tone="danger">{errorMessage(list.error)}</Notice>;
  if (list.data.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Spot name="history" />} title="Nothing here" />
      </Card>
    );
  }

  return (
    <>
      <ul className="stagger flex flex-col gap-3" data-testid="withdrawal-list">
        {list.data.map((w) => (
          <li key={w.id} data-testid="withdrawal-row">
            <Card className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-lg text-fg tabular-nums">
                    {formatAmount(w.amount, 8)} <span className="text-muted">{w.asset}</span>
                  </span>
                  <Badge>{w.chain}</Badge>
                  <Badge tone={W_TONE[w.status]}>{label(w.status)}</Badge>
                  <span className="text-sm text-muted">{usd(w.usdValueCents, "no price")}</span>
                </div>
                <p className="mt-1 text-sm text-muted">
                  <Link to={`/users/${w.userId}`} className="text-fg hover:underline">
                    {w.userEmail}
                  </Link>{" "}
                  · {formatDateTime(w.requestedAt)} · id {w.id.slice(0, 8)}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted">to</span> <Address value={w.toAddress} start={10} end={6} />
                  {w.explorerUrl && <ExternalLink href={w.explorerUrl}>Explorer</ExternalLink>}
                </div>
                {(w.lastError || w.failureReason) && (
                  <p className="mt-1 text-sm text-warning">{w.failureReason ?? w.lastError}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {canReview && w.status === "PENDING_APPROVAL" && (
                  <Button size="sm" onClick={() => open("approve", w)}>
                    Approve
                  </Button>
                )}
                {canReview && ["REQUESTED", "PENDING_APPROVAL", "QUEUED"].includes(w.status) && (
                  <Button size="sm" variant="secondary" onClick={() => open("reject", w)}>
                    Reject
                  </Button>
                )}
                {canMarkFailed && ["SIGNED", "BROADCAST"].includes(w.status) && (
                  <Button size="sm" variant="danger" onClick={() => open("fail", w)}>
                    Mark failed…
                  </Button>
                )}
              </div>
            </Card>
          </li>
        ))}
      </ul>

      <StepUpModal
        open={action?.kind === "approve"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Approve withdrawal"
        description={
          action ? `${action.w.amount} ${action.w.asset} on ${action.w.chain} for ${action.w.userEmail}` : ""
        }
        confirmLabel="Approve and queue"
        onConfirm={(code) => api.approveWithdrawal(action!.w.id, code)}
        onDone={refresh}
      >
        {action && <p className="font-mono text-xs break-all text-fg">To {action.w.toAddress}</p>}
      </StepUpModal>

      <StepUpModal
        open={action?.kind === "reject"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reject withdrawal"
        description="The amount and fee go back to the user's balance."
        confirmLabel="Reject and refund"
        danger
        canConfirm={reason.trim().length > 0}
        onConfirm={(code) => api.rejectWithdrawal(action!.w.id, reason.trim(), code)}
        onDone={refresh}
      >
        <Field label="Reason (the user sees this)">
          {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />}
        </Field>
      </StepUpModal>

      <StepUpModal
        open={action?.kind === "fail"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Mark as failed and refund?"
        confirmLabel="Mark failed and refund"
        danger
        canConfirm={typed === "REFUND"}
        onConfirm={(code) => api.markWithdrawalFailed(action!.w.id, typed, code)}
        onDone={refresh}
      >
        <Notice tone="danger" title="Danger — money can be paid twice">
          Only continue if the explorer shows this transaction does NOT exist and can no longer be included.
          If it confirms later, the user is paid twice.
        </Notice>
        {action?.w.explorerUrl && (
          <ExternalLink href={action.w.explorerUrl}>Check the explorer first</ExternalLink>
        )}
        <Field label='Type "REFUND" to confirm'>
          {(p) => (
            <Input {...p} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          )}
        </Field>
      </StepUpModal>
    </>
  );
}

export function DepositsPage() {
  const [status, setStatus] = useState("OPEN");
  return (
    <>
      <PageHeader title="Deposits" subtitle="Incoming transfers to users' deposit addresses." />
      <Segmented
        label="Filter"
        value={status}
        onChange={setStatus}
        className="mb-4"
        options={[
          { value: "OPEN", label: "Confirming" },
          { value: "CREDITED", label: "Credited" },
          { value: "DUST", label: "Dust" },
          { value: "ORPHANED", label: "Dropped" },
          { value: "ALL", label: "All" },
        ]}
      />
      <DepositList status={status} />
    </>
  );
}

export function DepositList({ status, userId }: { status: string; userId?: string }) {
  const list = useDeposits(status, userId);
  if (list.isPending) return <Skeleton className="h-40 rounded-card" />;
  if (list.error) return <Notice tone="danger">{errorMessage(list.error)}</Notice>;
  if (list.data.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Spot name="coins" />} title="Nothing here" />
      </Card>
    );
  }
  return (
    <ul className="stagger flex flex-col gap-3" data-testid="deposit-list">
      {list.data.map((d) => (
        <li key={d.id} data-testid="deposit-row">
          <Card className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-lg text-fg tabular-nums">
                {formatAmount(d.amount, 8)} <span className="text-muted">{d.asset}</span>
              </span>
              <Badge>{d.chain}</Badge>
              <Badge tone={D_TONE[d.status]}>
                {d.status === "CONFIRMING" || d.status === "DETECTED"
                  ? `${d.confirmations}/${d.requiredConfirmations} conf.`
                  : label(d.status)}
              </Badge>
            </div>
            <p className="text-sm text-muted">
              <Link to={`/users/${d.userId}`} className="text-fg hover:underline">
                {d.userEmail}
              </Link>{" "}
              · {formatDateTime(d.detectedAt)}
            </p>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted">tx</span> <Address value={d.txHash} start={10} end={6} />
              {d.explorerUrl && <ExternalLink href={d.explorerUrl}>Explorer</ExternalLink>}
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}
